import { injectPostgresEndpoints } from "./postgresApi";
import {
  Lesson,
  LessonCompleteRequest,
  LessonCompleteResponse,
} from "../../types/newtypes";
import { Course } from "../../types/types";
import { withCompletionIdentity, type CompletionIdentity } from "./completionRequest";

// Helper: normalize course object keys to camelCase expected by UI
function normalizeCourseShape(input: any): Course {
  if (!input) return input as Course;
  const base: any = {
    id: input.id,
    title: input.title,
    description: input.description,
    lessons: (input.lessons || []).map((l: any) => ({
      id: l.id,
      title: l.title,
      order: l.order,
      status: l.status,
    })),
  };

  // attach normalized fields once
  base.progress_percentage =
    input.progress_percentage !== undefined
      ? input.progress_percentage
      : input.progressPercentage ?? 0;

  base.is_completed =
    input.is_completed !== undefined
      ? input.is_completed
      : input.isCompleted ?? false;

  base.icon_uri =
    input.icon_uri !== undefined ? input.icon_uri : input.iconUri ?? null;

  base.reward_xp =
    input.reward_xp !== undefined ? input.reward_xp : input.rewardXp ?? 0;

  base.estimated_total_duration_minutes =
    input.estimated_total_duration_minutes !== undefined
      ? input.estimated_total_duration_minutes
      : input.estimatedTotalDurationMinutes ?? 0;

  base.levelNumber =
    input.levelNumber !== undefined
      ? input.levelNumber
      : input.level_number ?? 1;

  const rawSoftLock =
    input.softLock ||
    input.soft_lock ||
    (input.soft_lock_after_order
      ? {
          afterLessonOrder: input.soft_lock_after_order,
          message:
            input.soft_lock_message || input.softLockMessage || input.message,
        }
      : null);

  if (rawSoftLock) {
    const afterOrder = Number(
      rawSoftLock.afterLessonOrder ??
        rawSoftLock.after_lesson_order ??
        rawSoftLock.afterOrder ??
        rawSoftLock.after_order,
    );
    if (Number.isFinite(afterOrder)) {
      const rawMessage =
        rawSoftLock.message ??
        rawSoftLock.soft_lock_message ??
        rawSoftLock.softLockMessage;
      base.softLock = {
        afterLessonOrder: afterOrder,
        message:
          typeof rawMessage === "string" && rawMessage.trim().length > 0
            ? rawMessage.trim()
            : null,
      };
    }
  }

  return base as Course;
}

function normalizeCoursesArray(arr: any[]): Course[] {
  return (arr || []).map((c) => normalizeCourseShape(c));
}

// Card completion types
interface CardCompletionData {
  score: number;
  mistakes: number;
}

interface CardCompletionResponse {
  card_id: string;
  base_xp: number;
  earned_xp: number;
  xp_percentage: number;
  user_total_xp: number;
}

export const postgresCourseApi = injectPostgresEndpoints({
  endpoints: (builder) => ({
    // Get all courses (v9)
    getCourses: builder.query<Course[], void>({
      query: () => "/courses",
      providesTags: ["Courses"],
      transformResponse: (response: any) => normalizeCoursesArray(response),
    }),

    // Get single course (v9)
    getCourse: builder.query<Course, string>({
      query: (courseId) => `/courses/${courseId}`,
      providesTags: (result, error, courseId) => [
        { type: "Courses", id: courseId },
      ],
      keepUnusedDataFor: 120,
      transformResponse: (response: any) => normalizeCourseShape(response),
    }),

    // Get specific lesson (v9)
    getLesson: builder.query<Lesson, string>({
      query: (lessonId) => `/lessons/${lessonId}`,
      providesTags: (result, error, lessonId) => [
        { type: "Lessons", id: lessonId },
      ],
    }),

    // Submit lesson completion with card scores (v9)
    completeLesson: builder.mutation<
      LessonCompleteResponse,
      { lessonId: string; data: LessonCompleteRequest; identity: CompletionIdentity }
    >({
      queryFn: ({ lessonId, data, identity }, api) => withCompletionIdentity(identity, api, async (request) => {
        const result = await request({ url: `/progress/lessons/${lessonId}/complete`, method: "POST", body: data });
        return result.error ? result : { data: result.data as LessonCompleteResponse };
      }),
      invalidatesTags: (result, error, { lessonId }) => [
        { type: "Lessons", id: lessonId },
        { type: "Cards" },
        { type: "UserProgress" },
        { type: "Users", id: "ME" },
        "Courses",
        "MasterCourses",
      ],
    }),

    skipLesson: builder.mutation<LessonCompleteResponse, { lessonId: string; identity: CompletionIdentity }>({
      queryFn: ({ lessonId, identity }, api) => withCompletionIdentity(identity, api, async (request) => {
        const result = await request({ url: `/progress/lessons/${lessonId}/skip`, method: "POST" });
        return result.error ? result : { data: result.data as LessonCompleteResponse };
      }),
      invalidatesTags: (result, error, { lessonId }) => [
        { type: "Lessons", id: lessonId },
        { type: "Cards" },
        { type: "UserProgress" },
        { type: "Users", id: "ME" },
        "Courses",
        "MasterCourses",
        "Certificates",
        { type: "Certificates", id: "LIST" },
      ],
    }),

    // Submit card completion and retain per-card score metadata (v9)
    submitCardCompletion: builder.mutation<
      CardCompletionResponse,
      { cardId: string; data: CardCompletionData }
    >({
      query: ({ cardId, data }) => ({
        url: `/progress/cards/${cardId}/complete`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { cardId }) => [
        { type: "Cards", id: cardId },
        { type: "UserProgress" },
        { type: "Users", id: "ME" }
      ],
    }),
  }),
});

export const {
  useGetCoursesQuery,
  useGetCourseQuery,
  useGetLessonQuery,
  useCompleteLessonMutation,
  useSkipLessonMutation,
  useSubmitCardCompletionMutation,
} = postgresCourseApi;
